import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { Botao } from './base';
import { Confirmar } from './sobreposicao';
import { useToast } from './toast';
import { discretoAtivo } from '../nucleo/discreto';

/*
 * Copiar para a área de transferência (U14, review r66): saiu de `modules/integracoes/comum.tsx` para o núcleo, porque tela de
 * um módulo não importa tela de outro (o segredo do webhook da automação usa o mesmo arranjo das Integrações). As
 * Integrações reexportam daqui.
 */

/** Copia para a área de transferência, com plano B para contexto sem permissão do navegador. */
export async function copiarTexto(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    try {
      const area = document.createElement('textarea');
      area.value = texto;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const feito = document.execCommand('copy');
      area.remove();
      return feito;
    } catch {
      return false;
    }
  }
}

/**
 * Botão de copiar. `sensivel`: o texto traz dados reais (payload de webhook); com o modo discreto ligado, pede confirmação
 * antes de copiar, como a exportação.
 */
export function BotaoCopiar({ texto, rotulo, tamanho = 'sm', sensivel }: { texto: string; rotulo?: string; tamanho?: 'sm' | 'md'; sensivel?: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [copiado, setCopiado] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  async function copiar() {
    if (await copiarTexto(texto)) {
      setCopiado(true);
      toast.sucesso(t('comum.copiado'));
      setTimeout(() => setCopiado(false), 2000);
    } else toast.erro(t('comum.copiar'));
  }
  return (
    <>
      <Botao
        variante="secundario"
        tamanho={tamanho}
        aria-label={rotulo ?? t('comum.copiar')}
        icone={copiado ? <Check className="size-4 text-verde" aria-hidden /> : <Copy className="size-4" aria-hidden />}
        onClick={() => (sensivel && discretoAtivo() ? setConfirmar(true) : void copiar())}
      >
        {copiado ? t('comum.copiado') : (rotulo ?? t('comum.copiar'))}
      </Botao>
      {sensivel && (
        <Confirmar
          aberto={confirmar}
          aoFechar={() => setConfirmar(false)}
          titulo={t('discreto.copiarTitulo')}
          texto={<p data-aviso-copiar>{t('discreto.copiarAviso')}</p>}
          aoConfirmar={() => {
            setConfirmar(false);
            void copiar();
          }}
        />
      )}
    </>
  );
}

/** Texto de uma linha com botão de copiar (URL do webhook, URL do MCP, prefixo da chave). */
export function LinhaCopiavel({ texto, rotuloCopiar }: { texto: string; rotuloCopiar?: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-lg border border-borda bg-sup-2 px-3 py-2 font-mono text-xs text-tinta-1" title={texto}>
        {texto}
      </code>
      <BotaoCopiar texto={texto} rotulo={rotuloCopiar} />
    </div>
  );
}

